import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { ProductHeader } from "@/components/product/ProductHeader";
import { Gallery } from "@/components/product/Gallery";
import { BuyBox } from "@/components/product/BuyBox";
import { Related } from "@/components/product/Related";
import { CartDrawer } from "@/components/site/CartDrawer";
import { api } from "@/lib/api";

const NotFound = () => (
  <div className="flex min-h-[60vh] flex-col items-start justify-center gap-6 px-5 sm:px-8 lg:px-12" data-testid="product-not-found">
    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-smoke">Errore 404</p>
    <p className="font-display text-4xl font-black uppercase leading-none sm:text-5xl">Questo pezzo<br />non c&apos;è più.</p>
    <Link to="/#sfushop" className="inline-flex h-12 items-center rounded-full bg-acid px-7 font-mono text-xs uppercase tracking-[0.18em] text-ink">Torna allo shop</Link>
  </div>
);

export default function ProductPage() {
  const { slug } = useParams();
  const { data: product, isLoading, isError } = useQuery({
    queryKey: ["product", slug],
    queryFn: () => api.get(`/products/${slug}`).then((r) => r.data),
    retry: false,
  });

  useEffect(() => {
    if (!product?.name) return undefined;
    const previous = document.title;
    document.title = `${product.name} — sfushop`;
    return () => {
      document.title = previous;
    };
  }, [product?.name]);

  return (
    <div className="min-h-screen" data-testid="product-page">
      <ProductHeader />
      {isLoading ? (
        <div className="flex min-h-[60vh] items-center justify-center" data-testid="product-loading"><Loader2 className="h-8 w-8 animate-spin text-acid" /></div>
      ) : isError || !product ? (
        <NotFound />
      ) : (
        <>
          <main className="grid gap-12 px-5 py-10 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:px-12 lg:py-16">
            <div className="lg:col-span-7">
              <Gallery images={Array.isArray(product.images) ? product.images : [product.image]} name={product.name} />
            </div>
            <div className="lg:col-span-5">
              <BuyBox key={product.id} product={product} />
            </div>
          </main>
          <Related current={product} />
        </>
      )}
      <CartDrawer />
    </div>
  );
}
